/**
 * Sequencer - Logique de séquençage 16 steps
 * Gère 2x TB-303 + 1x TR-808 + 1x TR-909 + 1x Minimoog + 1x MS-20 + 1x Jupiter-8
 */

import * as Tone from 'tone';

export class Sequencer {
    constructor(store, tb303_1, tb303_2, tr808, tr909, transport, minimoog = null, ms20 = null, jupiter8 = null) {
        this.store = store;
        this.tb303_1 = tb303_1;
        this.tb303_2 = tb303_2;
        this.tr808 = tr808;
        this.tr909 = tr909;
        this.minimoog = minimoog;
        this.ms20 = ms20;
        this.jupiter8 = jupiter8;
        this.transport = transport;

        this.currentStep = 0;
        this.stepCallbacks = [];
        this.lastStep = -1; // Pour détecter la fin de bar (wrap de 15 à 0)

        // Speed multiplier tracking for Minimoog
        this.minimoogFractionalStep = 0;
        this.minimoogLastIntStep = -1;

        this.setupSequence();
    }

    /**
     * Ajoute le Minimoog au séquenceur (appelé après construction)
     */
    setMinimoog(minimoog) {
        this.minimoog = minimoog;
    }

    /**
     * Ajoute le MS-20 au séquenceur (appelé après construction)
     */
    setMS20(ms20) {
        this.ms20 = ms20;
    }

    /**
     * Ajoute le Jupiter-8 au séquenceur (appelé après construction)
     */
    setJupiter8(jupiter8) {
        this.jupiter8 = jupiter8;
    }

    /**
     * Ajoute l'ARP 2600 au séquenceur (appelé après construction)
     */
    setARP2600(arp2600) {
        this.arp2600 = arp2600;
    }

    /**
     * Ajoute le Juno-106 au séquenceur (appelé après construction)
     */
    setJuno106(juno106) {
        this.juno106 = juno106;
    }

    /**
     * Ajoute le SH-101 au séquenceur (appelé après construction)
     */
    setSH101(sh101) {
        this.sh101 = sh101;
    }

    /**
     * Ajoute le MiniMod au séquenceur (appelé après construction)
     */
    setMiniMod(minimod) {
        this.minimod = minimod;
    }

    setupSequence() {
        // Créer la séquence Tone.js
        this.sequence = new Tone.Sequence((time, step) => {
            this.currentStep = step;

            // Détecter la fin de bar (quand on passe de step 15 à step 0)
            // et avancer dans le song si le mode song est actif
            if (this.lastStep === 15 && step === 0) {
                this.store.advanceSongBar();
            }
            this.lastStep = step;

            // Notifier l'UI via le Draw scheduler
            Tone.Draw.schedule(() => {
                this.notifyStepChange(step);
            }, time);

            // Jouer les sons selon les patterns
            this.playStep(step, time);

        }, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], '16n');

        // Démarrer la séquence (elle suivra le transport)
        this.sequence.start(0);
    }

    playStep(step, time) {
        // En mode scene, chaque instrument peut jouer un pattern différent
        // getInstrumentPatterns() retourne le bon pattern selon le mode actif

        // TB-303 #1 (skip si powered off ou muté dans la scene)
        if (this.tb303_1.isPowered && !this.store.isInstrumentMutedInScene('tb303_1')) {
            const patterns = this.store.getInstrumentPatterns('tb303_1');
            if (patterns.bass1[step]) {
                const note = patterns.bass1Notes[step];
                const accent = patterns.bass1Accent[step];
                const slide = patterns.bass1Slide[step];
                this.tb303_1.triggerNote(note, { accent, slide, time });
            }
        }

        // TB-303 #2 (skip si powered off ou muté dans la scene)
        if (this.tb303_2.isPowered && !this.store.isInstrumentMutedInScene('tb303_2')) {
            const patterns = this.store.getInstrumentPatterns('tb303_2');
            if (patterns.bass2[step]) {
                const note = patterns.bass2Notes[step];
                const accent = patterns.bass2Accent[step];
                const slide = patterns.bass2Slide[step];
                this.tb303_2.triggerNote(note, { accent, slide, time });
            }
        }

        // TR-808 (skip si powered off ou muté dans la scene)
        if (this.tr808.isPowered && !this.store.isInstrumentMutedInScene('tr808')) {
            const patterns = this.store.getInstrumentPatterns('tr808');
            if (patterns.kick[step]) {
                this.tr808.triggerKick(time);
            }
            if (patterns.snare[step]) {
                this.tr808.triggerSnare(time);
            }
            if (patterns.clap[step]) {
                this.tr808.triggerClap(time);
            }
            if (patterns.rimshot[step]) {
                this.tr808.triggerRimshot(time);
            }
            if (patterns.hihat[step]) {
                this.tr808.triggerHihat(time);
            }
            if (patterns.openhh[step]) {
                this.tr808.triggerOpenHihat(time);
            }
            if (patterns.lowtom[step]) {
                this.tr808.triggerLowTom(time);
            }
            if (patterns.midtom[step]) {
                this.tr808.triggerMidTom(time);
            }
            if (patterns.hitom[step]) {
                this.tr808.triggerHiTom(time);
            }
            if (patterns.cowbell[step]) {
                this.tr808.triggerCowbell(time);
            }
        }

        // TR-909 (skip si powered off ou muté dans la scene)
        if (this.tr909.isPowered && !this.store.isInstrumentMutedInScene('tr909')) {
            const patterns = this.store.getInstrumentPatterns('tr909');
            if (patterns.kick909[step]) {
                this.tr909.triggerKick(time);
            }
            if (patterns.snare909[step]) {
                this.tr909.triggerSnare(time);
            }
            if (patterns.clap909[step]) {
                this.tr909.triggerClap(time);
            }
            if (patterns.rimshot909[step]) {
                this.tr909.triggerRimshot(time);
            }
            if (patterns.hh909[step]) {
                this.tr909.triggerHihat(time);
            }
            if (patterns.openhh909[step]) {
                this.tr909.triggerOpenHihat(time);
            }
            if (patterns.ride909[step]) {
                this.tr909.triggerRide(time);
            }
            if (patterns.crash909[step]) {
                this.tr909.triggerCrash(time);
            }
        }

        // Minimoog with speed multiplier (skip si powered off ou muté dans la scene)
        if (this.minimoog?.isPowered && !this.store.isInstrumentMutedInScene('minimoog')) {
            const patterns = this.store.getInstrumentPatterns('minimoog');
            if (patterns.minimoogGates) {
                this._playMinimoogWithSpeed(patterns, step, time);
            }
        }

        // MS-20 (skip si powered off ou muté dans la scene)
        if (this.ms20?.isPowered && !this.store.isInstrumentMutedInScene('ms20')) {
            const patterns = this.store.getInstrumentPatterns('ms20');
            if (patterns.ms20Gates && patterns.ms20Gates[step]) {
                const note = patterns.ms20Notes ? patterns.ms20Notes[step] : 'C2';
                this.ms20.triggerAttackRelease(note, '16n', time);
            }
        }

        // Jupiter-8 (skip si powered off ou muté dans la scene)
        if (this.jupiter8?.isPowered && !this.store.isInstrumentMutedInScene('jupiter8')) {
            const patterns = this.store.getInstrumentPatterns('jupiter8');
            if (patterns.jupiter8Gates && patterns.jupiter8Gates[step]) {
                const note = patterns.jupiter8Notes ? patterns.jupiter8Notes[step] : 'C3';
                this.jupiter8.triggerAttackRelease(note, '16n', time);
            }
        }

        // ARP 2600 (skip si powered off ou muté dans la scene)
        if (this.arp2600?.isPowered && !this.store.isInstrumentMutedInScene('arp2600')) {
            const patterns = this.store.getInstrumentPatterns('arp2600');
            if (patterns.arp2600Gates && patterns.arp2600Gates[step]) {
                const note = patterns.arp2600Notes ? patterns.arp2600Notes[step] : 'C3';
                this.arp2600.triggerAttackRelease(note, '16n', time);
            }
        }

        // Juno-106 (skip si powered off ou muté dans la scene)
        if (this.juno106?.isPowered && !this.store.isInstrumentMutedInScene('juno106')) {
            const patterns = this.store.getInstrumentPatterns('juno106');
            if (patterns.juno106Gates && patterns.juno106Gates[step]) {
                const note = patterns.juno106Notes ? patterns.juno106Notes[step] : 'C3';
                this.juno106.triggerAttackRelease(note, '16n', time);
            }
        }

        // SH-101 (skip si powered off ou muté dans la scene)
        if (this.sh101?.isPowered && !this.store.isInstrumentMutedInScene('sh101')) {
            const patterns = this.store.getInstrumentPatterns('sh101');
            if (patterns.sh101Gates && patterns.sh101Gates[step]) {
                const note = patterns.sh101Notes ? patterns.sh101Notes[step] : 'C2';
                this.sh101.triggerAttackRelease(note, '16n', time);
            }
        }

        // MiniMod (skip si powered off ou muté dans la scene)
        if (this.minimod?.isPowered && !this.store.isInstrumentMutedInScene('minimod')) {
            const patterns = this.store.getInstrumentPatterns('minimod');
            if (patterns.minimodGates && patterns.minimodGates[step]) {
                const note = patterns.minimodNotes ? patterns.minimodNotes[step] : 'C3';
                this.minimod.triggerAttackRelease(note, '16n', time);
            }
        }
    }

    /**
     * Play Minimoog with speed multiplier support
     * @param {Object} patterns - Pattern data
     * @param {number} mainStep - Current main sequencer step (0-15)
     * @param {number} time - Tone.js time
     */
    _playMinimoogWithSpeed(patterns, mainStep, time) {
        const speed = this.store.getSynthParam('minimoog', 'seqSpeed') || 1;
        const sixteenthDuration = Tone.Time('16n').toSeconds();

        if (speed <= 1) {
            // Slower speeds (0.25, 0.5, 1): advance fractional step
            this.minimoogFractionalStep += speed;
            const currentIntStep = Math.floor(this.minimoogFractionalStep) % 16;

            // Only trigger when we cross an integer boundary
            if (currentIntStep !== this.minimoogLastIntStep) {
                this.minimoogLastIntStep = currentIntStep;

                if (patterns.minimoogGates[currentIntStep]) {
                    const note = patterns.minimoogNotes ? patterns.minimoogNotes[currentIntStep] : 'C2';
                    // Duration scales inversely with speed
                    const duration = sixteenthDuration / speed;
                    this.minimoog.triggerAttackRelease(note, duration, time);
                }
            }

            // Wrap fractional step to prevent overflow
            if (this.minimoogFractionalStep >= 16) {
                this.minimoogFractionalStep -= 16;
            }
        } else {
            // Faster speeds (2, 4): trigger multiple notes per main step
            const notesPerStep = Math.round(speed);
            const subDuration = sixteenthDuration / notesPerStep;

            for (let i = 0; i < notesPerStep; i++) {
                const subStep = (mainStep * notesPerStep + i) % 16;
                const subTime = time + (i * subDuration);

                if (patterns.minimoogGates[subStep]) {
                    const note = patterns.minimoogNotes ? patterns.minimoogNotes[subStep] : 'C2';
                    this.minimoog.triggerAttackRelease(note, subDuration * 0.9, subTime);
                }
            }
        }
    }

    /**
     * Enregistre un callback pour les changements de step
     * @param {Function} callback - Fonction appelée avec le numéro de step
     */
    onStepChange(callback) {
        this.stepCallbacks.push(callback);
    }

    notifyStepChange(step) {
        this.stepCallbacks.forEach(cb => cb(step));
    }

    /**
     * Réinitialise le step courant et la position song
     */
    reset() {
        this.currentStep = 0;
        this.lastStep = -1;
        this.minimoogFractionalStep = 0;
        this.minimoogLastIntStep = -1;
        this.store.resetSongPosition();
    }

    getCurrentStep() {
        return this.currentStep;
    }
}
